import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Spin, Tabs, Table, DatePicker, Space, Button, Alert, Result, Tag, Radio, InputNumber, Tooltip, Modal, Input, Select, Switch } from 'antd';
import {
  LinkOutlined,
  DisconnectOutlined,
  CalendarOutlined,
  ReloadOutlined,
  CaretRightOutlined,
  CaretDownOutlined,
} from '@ant-design/icons';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Legend,
  Tooltip as RTooltip, ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import PageLayout from '../components/layout/PageLayout';
import AIChatBox from '../components/common/AIChatBox';
import api from '../libs/api';

// Format a numeric value as $X,XXX with negative values in red
function formatCurrency(value) {
  if (value === '' || value === null || value === undefined) return '-';
  const num = parseFloat(String(value).replace(/,/g, ''));
  if (isNaN(num)) return value;
  if (num === 0) return '-';
  const abs = Math.abs(num);
  const formatted = '$' + Math.round(abs).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (num < 0) {
    return <span style={{ color: '#cf1322' }}>-{formatted}</span>;
  }
  return formatted;
}

/**
 * A trading view of the P&L: the margin on goods alone, everything else beside it.
 *
 * Xero's Gross Profit answers a different question from the one the sales pages
 * answer, and the gap is not small. Freight income is coded to Other Income,
 * below Gross Profit, while freight expense sits in Cost of Sales above it — so
 * the reported margin carries the cost of delivery without the revenue that pays
 * for it. Merchant fees are in Cost of Sales too, against a surcharge recorded as
 * income. PW Renewable in September 2026 reports 6.1% on that basis and 8.7% on
 * the goods themselves, and in August the freight line alone is -$25,313.
 *
 * This is a rearrangement, not a restatement. Every line of Income, Other Income
 * and Cost of Sales lands in exactly one bucket, so the bottom row is always
 * Xero's own Gross Profit plus its Total Other Income — the table prints that
 * identity instead of asking to be trusted on it.
 *
 * Commission comes out of the goods margin as well, though it was not part of the
 * ask. It is a cost of winning the sale rather than of the equipment, and finance
 * already treats it that way: the Monthly Finance workbook's "Total Equipment GP"
 * row is 218,941 / 190,412 / 196,234 for Jul-Sep, which is what this view
 * produces only when commission is held out.
 */
/**
 * Card before cn, so "Credit Card Surcharge" is never read as a credit note.
 *
 * Credit notes need no side of their own: the section already says which kind
 * one is. An account in Income is a client credit note and comes off revenue;
 * the same account in Cost of Sales is a supplier credit note and comes off
 * cost. Nothing here invents those accounts — a chart that posts credit notes
 * straight back to the original sales and cost accounts, which is Xero's
 * default, leaves these buckets empty and the rows do not appear.
 */
const TRADE_BUCKETS = [
  ['freight',    /freight|cartage|courier/i],
  ['card',       /merchant fee|credit card|card surcharge/i],
  ['commission', /commission/i],
  ['cn',         /credit note|credit memo|\bcn\b/i],
];

function tradeBucket(name) {
  for (const [key, re] of TRADE_BUCKETS) if (re.test(name || '')) return key;
  return 'goods';
}

/**
 * Which side of the trading view a section belongs to.
 *
 * Named sections only. Anything unrecognised — Operating Expenses above all —
 * returns null and is left alone, because this view stops at gross margin.
 */
function tradeSection(title) {
  const t = (title || '').trim().toLowerCase().replace(/^(plus|less|add|add back|deduct)\s+/, '');
  if (['income', 'trading income', 'sales', 'revenue'].includes(t)) return 'income';
  if (['cost of sales', 'cost of goods sold'].includes(t)) return 'cogs';
  if (['other income', 'other revenue'].includes(t)) return 'other';
  return null;
}

const tradeNum = (v) => {
  if (v === '' || v == null) return 0;
  const s = String(v).replace(/[,$]/g, '').trim();
  const n = /^\(.*\)$/.test(s) ? -parseFloat(s.slice(1, -1)) : parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

function buildTradingView(rows) {
  const header = (rows || []).find((r) => r.type === 'header');
  const n = header ? header.cells.length - 1 : 0;
  if (n < 1) return null;

  const zero = () => new Array(n).fill(0);
  const acc = {
    goodsRev: zero(), goodsCost: zero(),
    clientCn: zero(), supplierCn: zero(),
    freightIn: zero(), freightCost: zero(),
    cardIn: zero(), cardCost: zero(),
    commission: zero(), otherIn: zero(),
  };

  let side = null;
  let seen = false;
  for (const r of rows) {
    if (r.type === 'section') { side = tradeSection(r.title); continue; }
    if (r.type !== 'row' || !side) continue;
    const bucket = tradeBucket(r.cells[0]);
    const target = side === 'cogs'
      ? ({
          freight: acc.freightCost, card: acc.cardCost,
          commission: acc.commission, cn: acc.supplierCn,
        }[bucket] || acc.goodsCost)
      : ({ freight: acc.freightIn, card: acc.cardIn, cn: acc.clientCn }[bucket]
         || (side === 'other' ? acc.otherIn : acc.goodsRev));
    const vals = r.cells.slice(1);
    for (let i = 0; i < n; i++) target[i] += tradeNum(vals[i]);
    seen = true;
  }
  if (!seen) return null;

  const sub = (a, b) => a.map((v, i) => v - b[i]);
  // Negating a zero gives -0, which prints as "-0" once it reaches a string.
  const neg = (a) => a.map((v) => (v === 0 ? 0 : -v));
  // Credit notes are added, not subtracted: a contra account already carries the
  // sign that reduces its own side, so the net is a plain sum and the view holds
  // whichever way the chart of accounts records them.
  const add = (a, b) => a.map((v, i) => v + b[i]);
  const netRev = add(acc.goodsRev, acc.clientCn);
  const netCost = add(acc.goodsCost, acc.supplierCn);
  const goodsGp = sub(netRev, netCost);
  const netFreight = sub(acc.freightIn, acc.freightCost);
  const netCard = sub(acc.cardIn, acc.cardCost);
  const netOther = netFreight.map((v, i) => v + netCard[i] - acc.commission[i] + acc.otherIn[i]);

  // The two credit note rows earn their place only when the chart of accounts
  // keeps them apart. Where it does not they are all zero, and showing them —
  // with the two extra subtotals they drag in — would be four rows of nothing.
  const hasCn = [...acc.clientCn, ...acc.supplierCn].some((v) => Math.round(v) !== 0);

  const lines = [
    { name: 'Goods revenue', values: acc.goodsRev },
    ...(hasCn ? [
      { name: 'Client credit notes', values: acc.clientCn },
      { name: 'Net goods revenue', values: netRev, kind: 'subtotal' },
    ] : []),
    { name: 'Goods cost of sales', values: neg(acc.goodsCost) },
    ...(hasCn ? [
      { name: 'Supplier credit notes', values: neg(acc.supplierCn) },
      { name: 'Net goods cost of sales', values: neg(netCost), kind: 'subtotal' },
    ] : []),
    { name: 'Goods gross profit', values: goodsGp, kind: 'subtotal' },
    {
      name: 'Goods GP %', kind: 'pct',
      values: goodsGp.map((v, i) => (netRev[i] ? (v / netRev[i]) * 100 : null)),
    },
    { name: 'Freight income', values: acc.freightIn, group: true },
    { name: 'Freight expense', values: neg(acc.freightCost) },
    { name: 'Net freight', values: netFreight, kind: 'subtotal' },
    { name: 'Card surcharge income', values: acc.cardIn, group: true },
    { name: 'Merchant fees', values: neg(acc.cardCost) },
    { name: 'Net card surcharge', values: netCard, kind: 'subtotal' },
    { name: 'Commission', values: neg(acc.commission), group: true },
    { name: 'Other income', values: acc.otherIn },
    { name: 'Net other income / (cost)', values: netOther, kind: 'subtotal' },
    {
      name: 'Goods GP + net other', kind: 'total', group: true,
      values: goodsGp.map((v, i) => v + netOther[i]),
    },
  ];

  return { lines: lines.map((l, i) => ({ key: i, ...l })), columns: header.cells };
}

/** The same view as plain text, so the AI analyst sees it too. */
function serializeTradingView(rows) {
  const view = buildTradingView(rows);
  if (!view) return '';
  const head = view.columns.slice(1).join(' | ');
  const body = view.lines.map((l) => {
    const vals = l.values.map((v) => (v == null ? '-'
      : l.kind === 'pct' ? `${v.toFixed(2)}%` : Math.round(v).toLocaleString()));
    return `${l.name}: ${vals.join(' | ')}`;
  }).join('\n');
  return `\n\n[Trading view — freight, card fees and commission held out of the goods margin]\n`
    + `(columns: ${head})\n${body}\n`
    + `The last row equals Xero's Gross Profit plus its Total Other Income.\n`;
}

// Serialize report rows to text for AI context
/**
 * The report as text for the AI, with each column's dates spelled out.
 *
 * The labels on their own are a trap. On an October 2026 report the column
 * marked FY27 covers July to October — four months — and the one marked FY26
 * is the finished year before it. Someone reading the page knows that; a model
 * handed five bare labels does not, and will answer a question about a year
 * out of whichever column carries its name. So the legend states the range and
 * the span for every column before any figure appears.
 */
function serializeReportRows(rows, reportName, columns) {
  if (!rows || rows.length === 0) return '';
  let text = `${reportName}\n`;

  if (columns?.length) {
    text += 'Columns, in the order they appear in every row below:\n';
    columns.forEach((c, i) => {
      const span = c.kind === 'month' ? 'one month'
        : c.kind === 'ytd' ? 'financial year to date — NOT a full year'
        : 'full financial year';
      text += `  ${i + 1}. ${c.label} = ${c.from} to ${c.to} (${span})\n`;
    });
    text += 'Quote the column label and its dates with any figure you take from it.\n';
  }

  const header = rows.find(r => r.type === 'header');
  if (header) text += '\n' + header.cells.join(' | ') + '\n' + '-'.repeat(80) + '\n';
  for (const r of rows) {
    if (r.type === 'header') continue;
    if (r.type === 'section') { text += `\n[${r.title}]\n`; continue; }
    if (r.cells) text += r.cells.join(' | ') + '\n';
  }
  return text;
}

/**
 * The working capital rows, when the panel below the trading view has them.
 *
 * Included because the questions people actually ask this box — can it service
 * its debt, where did the cash go — cannot be answered from a P&L, and the
 * model's previous answer to one of them was to apologise for the missing
 * balance sheet and then estimate around it.
 */
function serializeWorkingCapital(wc, tenantName) {
  if (!wc?.columns?.length) return '';
  const rowsOut = [
    ['Accounts receivable', wc.series?.ar],
    ['Accounts payable', wc.series?.ap],
    ['Inventory', wc.series?.inventory],
    ['Owed by group companies', wc.series?.intercoReceivable],
    ['Owed to group companies', wc.series?.intercoPayable],
  ].filter(([, v]) => Array.isArray(v) && v.some((x) => Math.round(x || 0) !== 0));
  if (!rowsOut.length) return '';

  const body = rowsOut.map(([name, vals]) => `${name}: `
    + vals.map((v) => (v == null ? 'n/a' : Math.round(v).toLocaleString())).join(' | ')).join('\n');

  return `\n\n[Balance sheet — ${tenantName}, position at the FIRST of each month]\n`
    + `(columns: ${wc.columns.join(' | ')})\n${body}\n`
    + 'These are point-in-time balances, not flows: do not add them to the P&L figures above '
    + 'or treat a change between two columns as a movement without saying so. '
    + 'Only these rows are available — no other balance sheet line is in this context.\n';
}

// Only P&L is surfaced; Balance Sheet and Bank Summary are no longer shown for
// any entity. Their endpoints still exist server-side.
const REPORT_TYPES = [
  { value: 'pl', label: 'Profit & Loss' },
];

const REPORT_ENDPOINTS = {
  pl: '/api/xero/profit-and-loss',
  bs: '/api/xero/balance-sheet',
  cf: '/api/xero/cashflow',
};

const REPORT_NAMES = {
  pl: 'Profit and Loss',
  bs: 'Balance Sheet',
  cf: 'Bank Summary',
};

// Periods run on the Jul–Jun financial year; quarters are fiscal quarters.
const TRACKING_PERIOD_OPTIONS = [
  { value: 'mtd',         label: 'MTD' },
  { value: 'qtd',         label: 'QTD' },
  { value: 'ytd',         label: 'YTD' },
  { value: 'lastMonth',   label: 'Last Month' },
  { value: 'lastQuarter', label: 'Last Quarter' },
  { value: 'lastYear',    label: 'Last Year' },
];

// The two column sets of the cost management table. Same category rows and
// target either way — only what's measured against them changes.
const OPEX_VIEWS = [
  { value: 'months', label: 'Compare with previous months' },
  { value: 'spread', label: 'Spread in cost centres' },
];

// ============================================
// Opex Management View (ShineHub only)
// ============================================
function OpexManagementView({ entity }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [targets, setTargets] = useState({}); // key → number (overrides backend defaults)

  // 'months' = compare with previous months (P&L report based)
  // 'spread'  = spread across cost centres (bill line tracking based)
  const [view, setView] = useState('months');
  const [tr, setTr] = useState(null);
  const [trLoading, setTrLoading] = useState(false);
  const [trError, setTrError] = useState(null);
  const [period, setPeriod] = useState('mtd');
  // Cost-centre spread is always operating expenses (Xero class EXPENSE minus
  // Direct Costs) — there is no picker for it
  const TRACKING_SCOPE = 'expense';
  const [selectedCentres, setSelectedCentres] = useState(null); // null = all
  const [hideEmptyRows, setHideEmptyRows] = useState(true);
  // Uncategorised sits below the Total as a note, expandable for review
  const [uncatOpen, setUncatOpen] = useState(false);

  // Overall AI summary chat dialog
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const chatRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/api/xero/opex-management', { params: { entity } });
      setData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load OpEx data');
    }
    setLoading(false);
  }, [entity]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Cost-centre data is only fetched once that view is actually opened
  useEffect(() => {
    if (view !== 'spread') return undefined;
    let cancelled = false;
    (async () => {
      setTrLoading(true); setTrError(null);
      try {
        const res = await api.get('/api/xero/opex-tracking', { params: { entity, period, scope: TRACKING_SCOPE } });
        if (cancelled) return;
        setTr(res.data.data);
        setSelectedCentres(null); // re-default to all whenever the option set may have changed
      } catch (err) {
        if (!cancelled) setTrError(err.response?.data?.message || 'Failed to load cost centre data');
      }
      if (!cancelled) setTrLoading(false);
    })();
    return () => { cancelled = true; };
  }, [view, entity, period]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [chatMessages, chatLoading]);

  const fmt$ = (v) => {
    if (v == null) return '-';
    if (v < 0) return <span style={{ color: '#cf1322' }}>{'-$' + Math.round(-v).toLocaleString()}</span>;
    return '$' + Math.round(v).toLocaleString();
  };

  const allCategories = (data?.categories || []).map(c => ({ ...c, key: c.key }));
  // Excluded from the table body and from every total — it isn't a real category
  const monthUncat = allCategories.find(c => c.key === 'uncategorised') || null;
  const rows = allCategories.filter(c => c.key !== 'uncategorised');

  // Build a compact context for the LLM (uses current edited targets if any)
  const buildOpexContext = () => {
    const active = rows.filter(c => c.key !== 'uncategorised' && !c.note);
    const lines = active.map(c => {
      const t = targets[c.key] != null ? targets[c.key] : (c.target || 0);
      const overUnder = t > 0 ? (c.mtd > t ? 'OVER' : 'under') : '';
      return `  ${c.name.padEnd(28)} target $${t.toLocaleString().padStart(7)} | MTD $${Math.round(c.mtd).toLocaleString().padStart(7)} ${overUnder.padEnd(5)} | LastMo $${Math.round(c.lastMonth).toLocaleString().padStart(7)} | LastQtrAvg/mo $${Math.round(c.lastQtrAvg).toLocaleString().padStart(7)} | YtdAvg/mo $${Math.round(c.ytdAvg).toLocaleString().padStart(7)}`;
    });
    const ignored = rows.filter(c => c.note).map(c => `  ${c.name} (${c.note}) — MTD $${Math.round(c.mtd).toLocaleString()}`);
    const uncat = rows.find(c => c.key === 'uncategorised');
    return [
      `ShineHub OpEx — month-to-date vs monthly target (entity: ${data?.tenantName || ''}):`,
      lines.join('\n'),
      ignored.length ? `\nIgnored (non-cash / one-off):\n${ignored.join('\n')}` : '',
      uncat && uncat.mtd ? `\nUncategorised MTD: $${Math.round(uncat.mtd).toLocaleString()} (${uncat.accounts?.length || 0} accounts not yet mapped)` : '',
    ].join('\n');
  };

  const sendChat = async (text, isInitial = false) => {
    const userMsg = { role: 'user', content: text };
    const next = isInitial ? [userMsg] : [...chatMessages, userMsg];
    setChatMessages(next);
    if (!isInitial) setChatInput('');
    setChatLoading(true);
    try {
      const res = await api.post('/api/xero/chat', {
        messages: next,
        reportContext: buildOpexContext(),
      });
      const reply = res.data.data?.message;
      if (reply) setChatMessages(prev => [...prev, reply]);
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, AI summary failed. Try again.' }]);
    }
    setChatLoading(false);
  };

  const openSummaryDialog = () => {
    setChatMessages([]);
    setChatOpen(true);
    sendChat(
      "Please review the ShineHub OpEx data above and produce a CFO-style overall summary. " +
      "Identify the **top 5 best-managed categories** (well under target, or trending down) and the " +
      "**top 5 categories needing most attention** (over target, trending up, or unusually high). " +
      "Cite specific $ figures and brief reasoning (1 line each). Don't list more than 5 per side. " +
      "After the lists, give one short paragraph of overall recommendation.",
      true
    );
  };

  // Uncategorised footnote: shown under the Total, never added into it
  const uncatToggle = (n) => (
    <span onClick={() => setUncatOpen(o => !o)} style={{ cursor: 'pointer', color: '#666' }}>
      {uncatOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}
      <span style={{ marginLeft: 6 }}>Uncategorised</span>
      <Tag color="default" style={{ marginLeft: 6, fontSize: 10 }}>not in Total</Tag>
      {n ? <span style={{ color: '#999', fontSize: 11 }}>{n} account{n === 1 ? '' : 's'} to assign</span> : null}
    </span>
  );

  const accountNote = (a) => (
    <span style={{ paddingLeft: 22, color: '#666', fontSize: 12 }}>
      {a.name || 'Account ' + a.code}
      {a.code ? <span style={{ color: '#bbb', marginLeft: 6, fontSize: 11 }}>{a.code}</span> : null}
    </span>
  );

  const targetCell = (v, rec) => (rec.key === 'uncategorised' || rec.key === '__uncat' || rec.isAccount)
    ? <span style={{ color: '#999' }}>{rec.isAccount ? '' : '-'}</span>
    : (
        <InputNumber
          value={targets[rec.key] != null ? targets[rec.key] : v}
          onChange={(val) => setTargets(prev => ({ ...prev, [rec.key]: val || 0 }))}
          formatter={x => '$' + (x ?? 0).toLocaleString()}
          parser={x => Number(String(x).replace(/[^\d.-]/g, ''))}
          style={{ width: '100%' }}
          size="small"
        />
      );

  const monthColumns = [
    {
      title: 'Category', dataIndex: 'name', width: 220,
      render: (v, rec) => {
        const tooltip = rec.accounts?.length
          ? (
              <div style={{ fontSize: 11, lineHeight: 1.5 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Aggregated Xero accounts:</div>
                {rec.accounts.map((a, i) => <div key={i}>{a.code ? `${a.code} – ` : ''}{a.name}</div>)}
              </div>
            )
          : 'No accounts mapped yet';
        return (
          <Tooltip title={tooltip} placement="right">
            <span style={{ cursor: 'help' }}>
              {v}
              {rec.note && <Tag color="default" style={{ marginLeft: 6, fontSize: 10 }}>{rec.note}</Tag>}
            </span>
          </Tooltip>
        );
      },
    },
    { title: 'Target', dataIndex: 'target', width: 130, align: 'right', render: targetCell },
    {
      title: 'This Month MTD', dataIndex: 'mtd', width: 140, align: 'right',
      render: (v, rec) => {
        const target = targets[rec.key] != null ? targets[rec.key] : rec.target;
        const over = target > 0 && v > target;
        return <span style={{ color: over ? '#cf1322' : undefined, fontWeight: over ? 600 : undefined }}>{fmt$(v)}</span>;
      },
    },
    {
      title: 'Δ vs Target', width: 110, align: 'right',
      render: (_, rec) => {
        const target = targets[rec.key] != null ? targets[rec.key] : rec.target;
        if (!target) return '-';
        const diff = (rec.mtd || 0) - target;
        const pct = (diff / target) * 100;
        const color = diff > 0 ? '#cf1322' : '#05A677';
        return <span style={{ color }}>{(diff > 0 ? '+' : '') + Math.round(pct) + '%'}</span>;
      },
    },
    { title: 'Last Month',         dataIndex: 'lastMonth',  width: 130, align: 'right', render: fmt$ },
    { title: 'Last QTR avg / mo',  dataIndex: 'lastQtrAvg', width: 150, align: 'right', render: fmt$ },
    { title: 'YTD avg / mo',       dataIndex: 'ytdAvg',     width: 130, align: 'right', render: fmt$ },
  ];

  // ---- Cost centre spread ----
  const untagged = tr?.untaggedKey || '(untagged)';
  const unitemised = tr?.unitemisedKey || '(not itemised)';
  const allCentres = tr?.columns || [];
  const centres = selectedCentres === null ? allCentres : allCentres.filter(c => selectedCentres.includes(c));
  const centreLineCounts = {};
  for (const d of (tr?.dimensions || [])) {
    if (d.name === tr?.dimension) for (const o of d.options) centreLineCounts[o.option] = o.lineCount;
  }

  // Row total follows the visible cost-centre columns so the table always foots
  const visibleTotal = (r) => centres.reduce((s, c) => s + (r.byOption?.[c] || 0), 0);
  const spreadAll = (tr?.rows || [])
    .map(r => {
      const children = (r.accounts || []).map(a => ({ ...a, isAccount: true, visibleTotal: visibleTotal(a) }));
      // Omit the key entirely when empty — an empty array still renders an
      // expand caret that opens onto nothing
      return { ...r, visibleTotal: visibleTotal(r), ...(children.length ? { children } : {}) };
    })
    .filter(r => !hideEmptyRows || r.visibleTotal !== 0);
  const spreadUncat = spreadAll.find(r => !r.categoryKey) || null;
  const spreadRows = spreadAll.filter(r => r.categoryKey);

  const spreadColumns = [
    {
      title: 'Category', dataIndex: 'name', width: 260, fixed: 'left',
      render: (name, rec) => rec.isAccount
        ? (
            <div style={{ lineHeight: 1.3 }}>
              <div>{name || <span style={{ color: '#999' }}>Account {rec.code}</span>}</div>
              {rec.code && (
                <div style={{ color: '#999', fontSize: 11 }}>
                  {rec.code}{rec.accountType ? ` · ${rec.accountType}` : ''}
                </div>
              )}
            </div>
          )
        : (
            <span>
              {name}
              {rec.note && <Tag color="default" style={{ marginLeft: 6, fontSize: 10 }}>{rec.note}</Tag>}
              <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>
                {rec.accounts?.length || 0} acct{(rec.accounts?.length || 0) === 1 ? '' : 's'}
              </span>
            </span>
          ),
    },
    { title: 'Target', dataIndex: 'target', width: 130, align: 'right', render: targetCell },
    {
      title: tr?.periodLabel || 'Selected period', width: 140, align: 'right',
      render: (_, rec) => {
        const target = targets[rec.key] != null ? targets[rec.key] : rec.target;
        const over = !rec.isAccount && target > 0 && rec.visibleTotal > target;
        return <b style={{ color: over ? '#cf1322' : undefined }}>{fmt$(rec.visibleTotal)}</b>;
      },
    },
    ...centres.map(c => ({
      title: c === unitemised
        ? (
            <Tooltip title="The part of the P&L that no fetched document accounts for — typically automated journals, payroll postings or opening balances. Kept as its own column so the row still adds up to the P&L.">
              <span style={{ color: '#999', cursor: 'help' }}>{c}</span>
            </Tooltip>
          )
        : c === untagged
          ? <span style={{ fontWeight: 400 }}>{c}</span>
          : <span>{c}<span style={{ color: '#999', fontWeight: 400, fontSize: 11, marginLeft: 4 }}>({centreLineCounts[c] || 0})</span></span>,
      width: 130, align: 'right',
      render: (_, rec) => (
        <span style={{ color: c === unitemised ? '#999' : undefined }}>
          {fmt$(rec.byOption?.[c])}
        </span>
      ),
    })),
  ];

  const centreTotal = (c) => spreadRows.reduce((s, r) => s + (r.byOption?.[c] || 0), 0);
  const spreadGrandTotal = centres.reduce((s, c) => s + centreTotal(c), 0);
  const spreadTargetTotal = spreadRows
    .filter(r => r.categoryKey)
    .reduce((s, r) => s + (targets[r.key] != null ? targets[r.key] : (r.target || 0)), 0);

  // Sum totals — excludes ignored (non-cash / one-off) rows. Uncategorised is
  // already out of `rows` entirely.
  const sumOf = (key) => rows
    .filter(r => !r.note)
    .reduce((s, r) => s + (r[key] || 0), 0);
  const sumTarget = rows
    .filter(r => !r.note)
    .reduce((s, r) => s + (targets[r.key] != null ? targets[r.key] : r.target), 0);
  const totalsRow = data ? {
    key: '__sum', name: 'Total (active categories)',
    target: sumTarget,
    mtd:         sumOf('mtd'),
    lastMonth:   sumOf('lastMonth'),
    lastQtrAvg:  sumOf('lastQtrAvg'),
    ytdAvg:      sumOf('ytdAvg'),
  } : null;

  return (
    <>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: 10 }}>
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space size="small" wrap>
            <Switch
              size="small"
              checked={view === 'spread'}
              onChange={(checked) => setView(checked ? 'spread' : 'months')}
            />
            <span style={{ fontSize: 12 }}>
              {view === 'spread' ? 'Spread in cost centres' : 'Compare with previous months'}
            </span>
            {data && (
              <span style={{ fontSize: 12, color: '#666' }}>
                · <b>{data.tenantName}</b> · MTD {data.periods?.mtd?.from} → {data.periods?.mtd?.to} ·
                Last QTR {data.periods?.last3Months?.from} → {data.periods?.last3Months?.to} ·
                YTD {data.periods?.ytd?.from} → {data.periods?.ytd?.to} ({data.ytdMonths} months)
              </span>
            )}
          </Space>
          <Button type="primary" size="small" onClick={openSummaryDialog} style={{ background: '#54A2CC', borderColor: '#54A2CC' }}>
            AI Summary
          </Button>
        </Space>
      </Card>
      <Card
        size="small"
        title={view === 'months' ? 'Cost Management' : (
          <Space wrap size="small" style={{ padding: '4px 0' }}>
            <Select size="small" value={period} onChange={setPeriod} style={{ width: 130 }}
              options={TRACKING_PERIOD_OPTIONS} />
            <Select
              size="small"
              mode="multiple"
              allowClear
              placeholder="All cost centres"
              value={selectedCentres === null ? allCentres : selectedCentres}
              onChange={setSelectedCentres}
              style={{ minWidth: 260, maxWidth: 460 }}
              maxTagCount="responsive"
              options={allCentres.map(c => ({
                value: c,
                label: (c === untagged || c === unitemised) ? c : `${c} (${centreLineCounts[c] || 0})`,
              }))}
              dropdownRender={(menu) => (
                <>
                  <div style={{ padding: '4px 8px', display: 'flex', gap: 12 }}>
                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setSelectedCentres(null)}>Select all</Button>
                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setSelectedCentres([])}>Clear</Button>
                  </div>
                  {menu}
                </>
              )}
            />
            <Button size="small" type={hideEmptyRows ? 'primary' : 'default'}
              style={hideEmptyRows ? { background: '#54A2CC', borderColor: '#54A2CC' } : undefined}
              onClick={() => setHideEmptyRows(v => !v)}>
              Hide $0 rows
            </Button>
            {tr && <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>{tr.periodFrom} → {tr.periodTo}</span>}
          </Space>
        )}
      >
        {view === 'spread' && (
          <>
            {trError && <Alert message={trError} type="error" showIcon style={{ marginBottom: 12 }} />}
            {tr && !tr.chartOfAccounts?.available && (
              <Alert
                type="warning" showIcon style={{ marginBottom: 12 }}
                message="Account names and the account filter need a Xero reconnect"
                description={
                  <span style={{ fontSize: 12 }}>
                    The chart of accounts is unreadable, so accounts show a bare code and every account is
                    included regardless of class.
                    {tr.chartOfAccounts?.reason ? ` (${tr.chartOfAccounts.reason})` : ''}
                    {' '}Use <b>Disconnect</b> then <b>Connect to Xero</b> above to re-consent with the new scope.
                  </span>
                }
              />
            )}
            {tr?.coverage && (
              <Alert
                type={tr.coverage.taggedPct < 50 ? 'warning' : 'info'}
                showIcon style={{ marginBottom: 12 }}
                message={
                  <span style={{ fontSize: 12 }}>
                    <b>{tr.periodLabel}</b> — totals come from the P&amp;L report and are split using{' '}
                    {tr.coverage.totalLines.toLocaleString()} ledger lines
                    {tr.ledger?.plAvailable && tr.ledger.itemisedPct != null && (
                      <> covering {tr.ledger.itemisedPct}% of ${Math.round(tr.ledger.plTotal).toLocaleString()}</>
                    )}
                    . {tr.coverage.taggedLines.toLocaleString()} of those lines carry a {tr.dimension} tag
                    ({tr.coverage.taggedPct}% of lines, {tr.coverage.taggedAmountPct}% of the itemised value);
                    the rest sits in <b>{untagged}</b>
                    {tr.ledger?.unitemisedTotal ? <> and <b>{unitemised}</b></> : null}.
                    {tr.scopeFilterActive && tr.coverage.excludedLines > 0 && (
                      <> {tr.coverage.excludedLines.toLocaleString()} lines fall outside <b>{tr.scopeLabel}</b> and are excluded.</>
                    )}
                  </span>
                }
                description={tr.ledger?.sources && (
                  <span style={{ fontSize: 11, color: '#666' }}>
                    {tr.ledger.sources.map(s => (
                      <span key={s.key} style={{ marginRight: 12, color: s.error ? '#cf1322' : undefined }}>
                        {s.label}: {s.error ? s.error : `${s.docCount.toLocaleString()} docs`}
                      </span>
                    ))}
                    {tr.ledger.fx && (
                      <span style={{ marginRight: 12 }}>
                        {tr.ledger.fx.lineCount} {tr.ledger.fx.currencies.join('/')} line
                        {tr.ledger.fx.lineCount === 1 ? '' : 's'} converted to AUD
                      </span>
                    )}
                    {!tr.ledger.plAvailable && (
                      <span style={{ color: '#cf1322' }}>
                        P&amp;L report unavailable{tr.ledger.plReason ? ` (${tr.ledger.plReason})` : ''} — totals fall back to the ledger sum.
                      </span>
                    )}
                  </span>
                )}
              />
            )}
          </>
        )}
        <Spin spinning={view === 'spread' ? trLoading : loading}>
          {view === 'months' ? (
            <Table
              dataSource={rows}
              columns={monthColumns}
              pagination={false}
              size="small"
              scroll={{ x: 1100 }}
              summary={totalsRow ? () => (
                <Table.Summary>
                  <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 600 }}>
                    <Table.Summary.Cell index={0}>{totalsRow.name}</Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">{fmt$(totalsRow.target)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">{fmt$(totalsRow.mtd)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={3} />
                    <Table.Summary.Cell index={4} align="right">{fmt$(totalsRow.lastMonth)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">{fmt$(totalsRow.lastQtrAvg)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">{fmt$(totalsRow.ytdAvg)}</Table.Summary.Cell>
                  </Table.Summary.Row>
                  {monthUncat && (
                    <>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>{uncatToggle(monthUncat.accounts?.length)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right"><span style={{ color: '#999' }}>-</span></Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">{fmt$(monthUncat.mtd)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={3} />
                        <Table.Summary.Cell index={4} align="right">{fmt$(monthUncat.lastMonth)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={5} align="right">{fmt$(monthUncat.lastQtrAvg)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={6} align="right">{fmt$(monthUncat.ytdAvg)}</Table.Summary.Cell>
                      </Table.Summary.Row>
                      {uncatOpen && (monthUncat.accounts || []).map(a => (
                        <Table.Summary.Row key={a.code || a.name}>
                          <Table.Summary.Cell index={0}>{accountNote(a)}</Table.Summary.Cell>
                          <Table.Summary.Cell index={1} />
                          <Table.Summary.Cell index={2} align="right">{fmt$(a.mtd)}</Table.Summary.Cell>
                          <Table.Summary.Cell index={3} />
                          <Table.Summary.Cell index={4} align="right">{fmt$(a.lastMonth)}</Table.Summary.Cell>
                          <Table.Summary.Cell index={5} align="right">{fmt$(a.lastQtrAvg)}</Table.Summary.Cell>
                          <Table.Summary.Cell index={6} align="right">{fmt$(a.ytdAvg)}</Table.Summary.Cell>
                        </Table.Summary.Row>
                      ))}
                    </>
                  )}
                </Table.Summary>
              ) : null}
            />
          ) : (
            <Table
              dataSource={spreadRows}
              columns={spreadColumns}
              pagination={false}
              size="small"
              scroll={{ x: 530 + centres.length * 130, y: 560 }}
              expandable={{ defaultExpandAllRows: false }}
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 600 }}>
                    <Table.Summary.Cell index={0}>Total (mapped categories)</Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">{fmt$(spreadTargetTotal)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">{fmt$(spreadGrandTotal)}</Table.Summary.Cell>
                    {centres.map((c, i) => (
                      <Table.Summary.Cell key={c} index={i + 3} align="right">{fmt$(centreTotal(c))}</Table.Summary.Cell>
                    ))}
                  </Table.Summary.Row>
                  {spreadUncat && (
                    <>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>{uncatToggle(spreadUncat.accounts?.length)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right"><span style={{ color: '#999' }}>-</span></Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">{fmt$(spreadUncat.visibleTotal)}</Table.Summary.Cell>
                        {centres.map((c, i) => (
                          <Table.Summary.Cell key={c} index={i + 3} align="right">{fmt$(spreadUncat.byOption?.[c])}</Table.Summary.Cell>
                        ))}
                      </Table.Summary.Row>
                      {uncatOpen && (spreadUncat.accounts || []).map(a => (
                        <Table.Summary.Row key={a.key}>
                          <Table.Summary.Cell index={0}>{accountNote(a)}</Table.Summary.Cell>
                          <Table.Summary.Cell index={1} />
                          <Table.Summary.Cell index={2} align="right">{fmt$(a.visibleTotal)}</Table.Summary.Cell>
                          {centres.map((c, i) => (
                            <Table.Summary.Cell key={c} index={i + 3} align="right">{fmt$(a.byOption?.[c])}</Table.Summary.Cell>
                          ))}
                        </Table.Summary.Row>
                      ))}
                    </>
                  )}
                </Table.Summary>
              )}
            />
          )}
        </Spin>
      </Card>

      <Modal
        open={chatOpen}
        onCancel={() => setChatOpen(false)}
        title="OpEx Summary & AI Analyst"
        width={720}
        footer={null}
      >
        <div ref={chatRef} style={{ maxHeight: 480, overflow: 'auto', marginBottom: 12, paddingRight: 4 }}>
          {chatMessages.map((m, i) => (
            <div key={i} style={{
              marginBottom: 12, display: 'flex',
              justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
            }}>
              <div style={{
                maxWidth: '90%', padding: '10px 14px', borderRadius: 10,
                fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap',
                background: m.role === 'user' ? '#54A2CC' : '#f5f5f5',
                color: m.role === 'user' ? '#fff' : '#333',
              }}>
                {m.content}
              </div>
            </div>
          ))}
          {chatLoading && (
            <div style={{ padding: '8px 12px', borderRadius: 10, background: '#f5f5f5', color: '#999', fontSize: 13, display: 'inline-block' }}>
              Analysing…
            </div>
          )}
        </div>
        <Input.TextArea
          value={chatInput}
          onChange={e => setChatInput(e.target.value)}
          onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); if (chatInput.trim()) sendChat(chatInput.trim()); } }}
          placeholder="Ask a follow-up question about these OpEx categories..."
          autoSize={{ minRows: 1, maxRows: 3 }}
          disabled={chatLoading}
        />
        <div style={{ marginTop: 8, textAlign: 'right' }}>
          <Button type="primary" onClick={() => { if (chatInput.trim()) sendChat(chatInput.trim()); }} loading={chatLoading}
            style={{ background: '#54A2CC', borderColor: '#54A2CC' }}>
            Send
          </Button>
        </div>
      </Modal>
    </>
  );
}


// ============================================
// Per-Tenant Report Tab Component
// ============================================
function ReportTab({ tenantId, tenantName }) {
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [reportType, setReportType] = useState('pl');

  // ShineHub additionally gets Opex Management; with a single option the toggle
  // is hidden rather than rendered as one lone button.
  const reportOptions = /shine\s*hub/i.test(tenantName || '')
    ? [...REPORT_TYPES, { value: 'opex', label: 'Opex Management' }]
    : REPORT_TYPES;

  /**
   * PW Renewable opens on the trading view, everyone else on the statement.
   *
   * PW is the entity the split actually distorts: it bills freight on nearly
   * every order, so its reported gross margin is the one that reads several
   * points below what the goods earn. The others carry little or no freight,
   * where the two views say close to the same thing and the familiar statement
   * is the better thing to land on.
   */
  const [plView, setPlView] = useState(() => (/\bpw\b/i.test(tenantName || '') ? 'trading' : 'pl'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({ _default: true });

  const toggleSection = (title) => {
    setCollapsedSections((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const fetchReport = useCallback(
    async (month, type) => {
      if (!tenantId || !month) return;
      if (!REPORT_ENDPOINTS[type]) return; // opex handled by OpexManagementView
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(REPORT_ENDPOINTS[type], {
          params: { tenantId, month: month.format('YYYY-MM') },
        });
        setData(res.data.data);
      } catch (err) {
        setError(err.response?.data?.message || `Failed to load ${REPORT_NAMES[type]} report`);
      }
      setLoading(false);
    },
    [tenantId]
  );

  /**
   * Credit notes, fetched beside the P&L rather than inside it.
   *
   * Its own request because the statement does not need it and should not wait
   * for it: five more Xero calls against a sixty-a-minute budget. A failure
   * here leaves the P&L and the trading view intact and the memo block absent,
   * which is the right way round — these figures annotate the statement, they
   * are not part of it.
   */
  const [cn, setCn] = useState(null);
  const fetchCreditNotes = useCallback(async (month) => {
    if (!tenantId || !month) return;
    setCn(null);
    try {
      const res = await api.get('/api/xero/credit-notes', {
        params: { tenantId, month: month.format('YYYY-MM') },
      });
      setCn(res.data.data);
    } catch {
      setCn(null);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchReport(selectedMonth, reportType);
    if (reportType === 'pl') fetchCreditNotes(selectedMonth);
  }, [selectedMonth, reportType, fetchReport, fetchCreditNotes]);

  // Reset collapsed sections when data changes
  useEffect(() => {
    if (!data?.rows) return;
    const sections = data.rows.filter((r) => r.type === 'section').map((r) => r.title);
    setCollapsedSections((prev) => {
      if (prev._default) {
        const init = {};
        sections.forEach((s) => { init[s] = true; });
        return init;
      }
      return prev;
    });
  }, [data]);

  // Reset collapsed when report type changes
  useEffect(() => {
    setCollapsedSections({ _default: true });
  }, [reportType]);

  // Held steady across renders because the trading view memoises off it; a fresh
  // [] each time would rebuild that on every keystroke in the AI chat box.
  const rows = useMemo(() => data?.rows || [], [data]);

  // Build columns from header row
  const headerRow = rows.find((r) => r.type === 'header');
  const columns = headerRow
    ? headerRow.cells.map((cell, i) => ({
        title: cell || '',
        dataIndex: `col${i}`,
        key: `col${i}`,
        align: i === 0 ? 'left' : 'right',
        width: i === 0 ? 300 : 140,
        render: (val, record) => {
          if (i === 0 && record._type === 'section') {
            const icon = collapsedSections[val]
              ? <CaretRightOutlined style={{ marginRight: 6 }} />
              : <CaretDownOutlined style={{ marginRight: 6 }} />;
            return <span>{icon}{val}</span>;
          }
          if (i === 0) return val;
          if (record._type === 'section') return '';
          return formatCurrency(val);
        },
      }))
    : [];

  // Build table data — flat sections, collapse hides detail rows but keeps summaries
  let currentSection = '';
  const tableData = rows
    .filter((r) => r.type !== 'header')
    .map((r, idx) => {
      const row = { key: idx, _type: r.type };
      if (r.type === 'section') {
        currentSection = r.title;
        row.col0 = r.title;
        row._section = r.title;
      } else {
        row._section = currentSection;
        (r.cells || []).forEach((cell, i) => {
          row[`col${i}`] = cell;
        });
      }
      return row;
    })
    .filter((row) => {
      // When section is collapsed, hide detail rows but keep summary/total
      if (collapsedSections[row._section] && row._type === 'row') return false;
      return true;
    });

  // The trading view is only offered when the report it rearranges is actually
  // there — a tenant whose P&L has no Income or Cost of Sales section gets the
  // statement and no toggle, rather than a toggle onto an empty card.
  const tradingView = useMemo(
    () => (reportType === 'pl' ? buildTradingView(rows) : null),
    [rows, reportType]
  );
  const showTrading = !!tradingView && plView === 'trading';

  // Serialize report for AI context — both views, whichever one is on screen,
  // so a question about freight is answerable without switching first.
  const [wcData, setWcData] = useState(null);
  const handleWcLoaded = useCallback((d) => setWcData(d), []);
  // A tenant's working capital must not outlive the tenant or the month it was
  // read for; without this the context would quietly describe the last one.
  useEffect(() => { setWcData(null); }, [tenantId, selectedMonth, reportType]);

  const reportContext = serializeReportRows(
    rows, `${tenantName} — ${REPORT_NAMES[reportType]}`, data?.columns,
  )
    + (reportType === 'pl' ? serializeTradingView(rows) : '')
    + (reportType === 'pl' ? serializeWorkingCapital(wcData, tenantName) : '');

  return (
    <>
      {error && (
        <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }} onClose={() => setError(null)} />
      )}

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle" align="center">
          <CalendarOutlined style={{ color: '#54A2CC' }} />
          <DatePicker
            picker="month"
            value={selectedMonth}
            onChange={(date) => date && setSelectedMonth(date)}
            format="MMM YYYY"
            size="small"
            allowClear={false}
          />
          {reportOptions.length > 1 && (
            <Radio.Group
              value={reportType}
              onChange={e => setReportType(e.target.value)}
              size="small"
              optionType="button"
              buttonStyle="solid"
              options={reportOptions}
            />
          )}
          <Button size="small" icon={<ReloadOutlined />} onClick={() => fetchReport(selectedMonth, reportType)}>
            Refresh
          </Button>
          {tradingView && (
            <Radio.Group
              value={plView}
              onChange={(e) => setPlView(e.target.value)}
              size="small"
              optionType="button"
              buttonStyle="solid"
              options={[
                { value: 'pl', label: 'P&L' },
                { value: 'trading', label: 'Trading View' },
              ]}
            />
          )}
        </Space>
      </Card>

      {reportType === 'opex' && /shine\s*hub/i.test(tenantName || '') && (
        <OpexManagementView entity="sh" />
      )}

      {reportType !== 'opex' && (
      <Spin spinning={loading}>
        {tableData.length > 0 ? (
          showTrading ? (
            <>
              <TradingView view={tradingView} tenantName={tenantName} cn={cn} />
              {/*
                * Fetched on its own, below the trading table.
                *
                * Twelve balance sheets is twelve Xero calls, and the statement
                * must not wait on them — nor disappear if they fail. Keyed on
                * the tenant and the month so switching either refetches, and
                * cached for half an hour on the server because a balance sheet
                * from eleven months ago does not move.
                */}
              <WorkingCapital
                tenantId={tenantId}
                tenantName={tenantName}
                anchorMonth={selectedMonth}
                onLoaded={handleWcLoaded}
              />
            </>
          ) : (
          <Card title={`${tenantName} — ${REPORT_NAMES[reportType]}`} size="small">
            <Table
              dataSource={tableData}
              columns={columns}
              pagination={false}
              size="small"
              scroll={{ x: 1000 }}
              onRow={(record) => ({
                onClick: record._type === 'section' ? () => toggleSection(record.col0) : undefined,
                style: record._type === 'section' ? { cursor: 'pointer', userSelect: 'none' } : {},
              })}
              rowClassName={(record) => {
                const KEY_ROWS = [
                  'Total Income', 'Gross Profit', 'Total Operating Expenses', 'Net Profit',
                  'Total Assets', 'Total Liabilities', 'Net Assets', 'Total Equity',
                  'Total Current Assets', 'Total Non-Current Assets',
                  'Total Current Liabilities', 'Total Non-Current Liabilities',
                ];
                if (record._type === 'section') return 'xero-section-row';
                if (KEY_ROWS.includes(record.col0)) return 'xero-key-row';
                if (record._type === 'summary' || record._type === 'total') return 'xero-key-row';
                return '';
              }}
            />
          </Card>
          )
        ) : !loading ? (
          <Card>
            <Result status="info" title="No Data" subTitle={`Select a month to view the ${REPORT_NAMES[reportType]} report.`} />
          </Card>
        ) : null}
      </Spin>
      )}

      {reportType !== 'opex' && (
        <AIChatBox
          endpoint="/api/xero/chat"
          contextBuilder={() => reportContext}
          title="Financial Analyst AI"
          placeholder="Ask about this report, e.g. 'What are the key trends?'"
        />
      )}
    </>
  );
}

// ============================================
// Trading View — goods margin on its own
// ============================================
/**
 * Credit notes as a memo, below the margin rather than inside it.
 *
 * Both kinds are already in the numbers above — Xero posts a credit note back
 * to the account it reverses — so taking them off again here would count them
 * twice. They are shown because neither is visible anywhere else on the
 * statement, and marked as memo because the arithmetic above is complete
 * without them.
 *
 * The supplier line needs the second row under it. For PW almost none of it
 * reaches the P&L at all: over Jul-Sep 2026, $826,010 of $826,995 credited by
 * suppliers went to Stock On Hand, because crediting a stock asset is not a
 * cost of sale and does not become one until the goods are sold. Printing the
 * headline figure without saying that would invite exactly the subtraction the
 * split exists to prevent.
 */
function memoLines(view, cn) {
  if (!cn) return [];
  const width = view.columns.length - 1;
  const fits = (a) => Array.isArray(a) && a.length === width;
  if (!fits(cn.clientCn) || !fits(cn.supplierCn)) return [];

  const lines = [
    { name: 'Netted into the figures above', kind: 'memoHead', group: true, values: new Array(width).fill(null) },
    { name: 'Client credit notes', kind: 'memo', values: cn.clientCn },
    { name: 'Supplier credit notes', kind: 'memo', values: cn.supplierCn },
  ];
  if (fits(cn.supplierCnInPL)) {
    lines.push({
      name: '— of which reached the P&L', kind: 'memo', values: cn.supplierCnInPL,
    });
  }
  return lines;
}

/**
 * Working capital beside the trading it came from.
 *
 * The trading view answers what a month earned. These three answer what it is
 * owed, what it owes and what it is holding — which move on their own clock and
 * are the first place a month that traded well still runs out of money. Read at
 * the first of each month for a year, so the shape is visible rather than a
 * single point that could be anything.
 *
 * Each figure carries the account names behind it. A balance sheet is matched
 * here on names rather than codes, because the group's organisations do not
 * share a numbering scheme, and a silent miss on that match would look exactly
 * like a company that owes nothing.
 */
const WC_SERIES = [
  { key: 'ar', label: 'Accounts receivable', colour: '#54A2CC', hint: 'owed to this company' },
  { key: 'ap', label: 'Accounts payable', colour: '#FA5252', hint: 'owed by this company' },
  { key: 'inventory', label: 'Inventory', colour: '#05A677', hint: 'stock on hand' },
];
const INTERCO_SERIES = [
  { key: 'intercoReceivable', label: 'Owed by group companies', colour: '#2F7FA8' },
  { key: 'intercoPayable', label: 'Owed to group companies', colour: '#C96A1E' },
];

const wcMonthLabel = (iso) => dayjs(iso).format('D MMM YY');

function WcChart({ title, columns, series, data, extra }) {
  const points = columns.map((c, i) => {
    const p = { label: dayjs(c).format('MMM YY') };
    for (const s of series) p[s.key] = data[s.key]?.[i] ?? null;
    return p;
  });
  const money = (v) => {
    if (v == null) return '-';
    const a = Math.abs(v);
    if (a >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (a >= 1_000) return `$${Math.round(v / 1_000)}K`;
    return `$${Math.round(v)}`;
  };
  return (
    <Card size="small" title={title} extra={extra} style={{ height: '100%' }}>
      <ResponsiveContainer width="100%" height={210}>
        <LineChart data={points} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
          <CartesianGrid stroke="#EEF1F5" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#667085' }} axisLine={false} tickLine={false} interval={0} angle={-35} textAnchor="end" height={46} />
          <YAxis tickFormatter={money} tick={{ fontSize: 10, fill: '#667085' }} axisLine={false} tickLine={false} width={56} />
          <RTooltip
            formatter={(v, n) => [formatCurrency(v), n]}
            contentStyle={{ fontSize: 12, borderColor: '#EEF1F5' }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {series.map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={s.colour}
              strokeWidth={2}
              dot={{ r: 2 }}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}

function WorkingCapital({ tenantId, tenantName, anchorMonth, onLoaded }) {
  const [wc, setWc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!tenantId || !anchorMonth) return undefined;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api.get('/api/xero/working-capital', {
      params: { tenantId, anchor: anchorMonth.format('YYYY-MM'), months: 12 },
    })
      .then((res) => {
        if (cancelled) return;
        setWc(res.data.data);
        // Handed up so the AI context can carry it; the panel is the only
        // thing that fetches it, and a question about debt service needs it.
        onLoaded?.(res.data.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e.response?.data?.message || 'Failed to load the balance sheet history');
        setWc(null);
        onLoaded?.(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tenantId, anchorMonth, onLoaded]);

  const columns = wc?.columns || [];
  const series = wc?.series || {};
  const accounts = wc?.accounts || {};

  // Inventory is shown where the organisation actually keeps stock, judged by
  // whether its own sheet carries such an account — not by which entity this is.
  const hasInventory = (accounts.inventory || []).length > 0
    || (series.inventory || []).some((v) => Math.round(v || 0) !== 0);
  const wcRows = WC_SERIES.filter((s) => s.key !== 'inventory' || hasInventory);

  const hasInterco = [...(series.intercoReceivable || []), ...(series.intercoPayable || [])]
    .some((v) => Math.round(v || 0) !== 0);

  const net = columns.map((_, i) => {
    const r = series.intercoReceivable?.[i];
    const p = series.intercoPayable?.[i];
    if (r == null && p == null) return null;
    return (r || 0) - (p || 0);
  });

  const tableRows = [
    ...wcRows.map((s) => ({ key: s.key, name: s.label, hint: s.hint, values: series[s.key] || [], colour: s.colour })),
    ...(hasInterco || (accounts.intercoReceivable || []).length || (accounts.intercoPayable || []).length ? [
      { key: 'ir', name: 'Owed by group companies', hint: 'intercompany, asset side', values: series.intercoReceivable || [], colour: '#2F7FA8', group: true },
      { key: 'ip', name: 'Owed to group companies', hint: 'intercompany, liability side', values: series.intercoPayable || [], colour: '#C96A1E' },
      { key: 'in', name: 'Net intercompany position', values: net, strong: true },
    ] : []),
  ];

  const tableColumns = [
    {
      title: 'Balance sheet, at the 1st',
      dataIndex: 'name',
      key: 'name',
      width: 240,
      fixed: 'left',
      render: (v, r) => (
        <span style={{ fontWeight: r.strong ? 600 : 400 }}>
          {r.colour && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: r.colour, marginRight: 8 }} />}
          {v}
          {r.hint && <span style={{ color: '#9AA3B2', fontSize: 11 }}> · {r.hint}</span>}
        </span>
      ),
    },
    ...columns.map((c, i) => ({
      title: wcMonthLabel(c),
      dataIndex: `c${i}`,
      key: `c${i}`,
      align: 'right',
      width: 110,
      render: (_, r) => (r.values[i] == null ? <span style={{ color: '#9AA3B2' }}>n/a</span> : formatCurrency(r.values[i])),
    })),
  ];

  const sourceNote = (label, names) => (
    names.length
      ? `${label}: ${names.join(', ')}`
      : `${label}: no account on this balance sheet matched`
  );

  return (
    <Card
      size="small"
      style={{ marginTop: 16 }}
      title={`${tenantName} — Working capital, 12 months to ${anchorMonth.format('MMM YYYY')}`}
    >
      <Spin spinning={loading}>
        {err && <Alert type="warning" showIcon message={err} style={{ marginBottom: 12 }} />}

        {wc?.missing?.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`${wc.missing.length} of 12 balance sheets could not be read`}
            description={`Xero did not return a report for ${wc.missing.join(', ')}. Those columns show n/a rather than zero, so a failed call cannot be mistaken for a nil balance.`}
          />
        )}

        {columns.length > 0 && (
          <>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
              <div style={{ flex: '1 1 380px', minWidth: 320 }}>
                <WcChart
                  title="Receivables, payables and stock"
                  columns={columns}
                  series={wcRows}
                  data={series}
                />
              </div>
              {(hasInterco || (accounts.intercoReceivable || []).length > 0 || (accounts.intercoPayable || []).length > 0) && (
                <div style={{ flex: '1 1 380px', minWidth: 320 }}>
                  <WcChart
                    title="Intercompany balances"
                    columns={columns}
                    series={INTERCO_SERIES}
                    data={series}
                  />
                </div>
              )}
            </div>

            <Table
              dataSource={tableRows}
              columns={tableColumns}
              pagination={false}
              size="small"
              scroll={{ x: 240 + columns.length * 110 }}
              rowClassName={(r) => (r.strong ? 'xero-key-row' : '')}
              onRow={(r) => ({ style: r.group ? { borderTop: '1px solid #e8e8e8' } : {} })}
            />

            <div style={{ marginTop: 8, fontSize: 12, color: '#888', lineHeight: 1.7 }}>
              Each column is the balance sheet dated the first of that month, so it
              already contains anything posted on the 1st — the same figure Xero gives
              for that date, not the previous month&apos;s close.
              {' '}Accounts are matched by name, because the group&apos;s organisations do
              not share a code scheme.
              {' '}{sourceNote('Receivables', accounts.ar || [])}.
              {' '}{sourceNote('Payables', accounts.ap || [])}.
              {hasInventory && <> {sourceNote('Inventory', accounts.inventory || [])}.</>}
              {' '}{sourceNote('Owed by group companies', accounts.intercoReceivable || [])}.
              {' '}{sourceNote('Owed to group companies', accounts.intercoPayable || [])}.
              {' '}An intercompany row has to both read like a loan and name a group
              company, and which way it runs is taken from the side of the sheet it
              sits on rather than from its wording.
              {wc?.otherLoans?.length > 0 && (
                <>
                  {' '}
                  <b>
                    {wc.otherLoans.length} other loan account
                    {wc.otherLoans.length === 1 ? '' : 's'} on this sheet
                  </b>
                  {' '}matched neither test and are in none of the figures above:
                  {' '}{wc.otherLoans.join(', ')}. If any of those is in fact a group
                  balance, say so and it can be named explicitly.
                </>
              )}
            </div>
          </>
        )}

        {!loading && !err && columns.length === 0 && (
          <div style={{ color: '#9AA3B2', padding: '24px 0', textAlign: 'center' }}>
            No balance sheet history available
          </div>
        )}
      </Spin>
    </Card>
  );
}

function TradingView({ view, tenantName, cn }) {
  const memo = memoLines(view, cn);
  const columns = view.columns.map((cell, i) => ({
    title: cell || '',
    dataIndex: `col${i}`,
    key: `col${i}`,
    align: i === 0 ? 'left' : 'right',
    width: i === 0 ? 300 : 140,
    render: (_, record) => {
      if (i === 0) return record.name;
      const v = record.values[i - 1];
      if (record.kind === 'pct') {
        if (v == null) return '-';
        return <span style={{ color: v < 0 ? '#cf1322' : undefined }}>{v.toFixed(2)}%</span>;
      }
      return formatCurrency(v);
    },
  }));

  return (
    <Card title={`${tenantName} — Trading View`} size="small">
      <Table
        dataSource={[...view.lines, ...memo.map((l, i) => ({ key: `memo${i}`, ...l }))]}
        columns={columns}
        pagination={false}
        size="small"
        scroll={{ x: 1000 }}
        rowClassName={(r) => {
          if (r.kind === 'subtotal' || r.kind === 'total') return 'xero-key-row';
          if (r.kind === 'memoHead') return 'xero-section-row';
          return '';
        }}
        onRow={(r) => ({
          style: {
            ...(r.group ? { borderTop: '1px solid #e8e8e8' } : {}),
            ...(r.kind === 'memo' ? { color: '#8c8c8c' } : {}),
          },
        })}
      />
      <div style={{ marginTop: 8, fontSize: 12, color: '#888', lineHeight: 1.7 }}>
        Freight expense and merchant fees come out of Cost of Sales, and their income
        out of Income and Other Income, so the goods margin is equipment only.
        Commission is held out too — it buys the sale, not the hardware.
        {view.lines.some((l) => /credit note/i.test(l.name))
          ? ' Credit notes are shown on their own line on each side, client against revenue and supplier against cost.'
          : ' Credit notes have no line of their own here, because this chart of accounts posts them back to the sales and cost accounts they reverse.'}
        {' '}Every line of Income, Other Income and Cost of Sales is counted exactly once,
        so the last row is Xero's own Gross Profit plus its Total Other Income.
        {memo.length > 0 && (
          <>
            {' '}The memo rows are read from the credit notes themselves and are already
            inside the figures above, so they are not subtracted again. A supplier credit
            mostly reduces stock rather than cost of sales, which is why its second row
            says how much of it reached the P&amp;L at all.
          </>
        )}
      </div>
    </Card>
  );
}

// ============================================
// Main Page
// ============================================
export default function XeroPL() {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [connectError, setConnectError] = useState(null);

  const checkStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await api.get('/api/xero/status');
      setConnectionStatus(res.data.data);
    } catch {
      setConnectionStatus({ connected: false, tenants: [] });
    }
    setStatusLoading(false);
  }, []);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === 'true') {
      checkStatus();
      window.history.replaceState({}, '', '/xero-pl');
    }
    if (params.get('error')) {
      setConnectError(decodeURIComponent(params.get('error')));
      window.history.replaceState({}, '', '/xero-pl');
    }
  }, [checkStatus]);

  const handleConnect = () => {
    const token = localStorage.getItem('token');
    const backendUrl = process.env.REACT_APP_API_URL || '';
    window.location.href = `${backendUrl}/api/xero/connect?token=${token}`;
  };

  const handleDisconnect = async () => {
    try {
      await api.post('/api/xero/disconnect');
      setConnectionStatus({ connected: false, tenants: [] });
    } catch {
      // handled by interceptor
    }
  };

  const isConnected = connectionStatus?.connected === true;
  const tenants = connectionStatus?.tenants || [];

  const tabItems = tenants.map((t) => ({
    key: t.tenantId,
    label: t.tenantName,
    children: <ReportTab tenantId={t.tenantId} tenantName={t.tenantName} />,
  }));

  const roleType = localStorage.getItem('roleType') || '';
  const canManageConnection = ['SUPER', 'ADMIN'].includes(roleType);

  return (
    <PageLayout>
      <div className="page-header">
        <h1>Xero Financial Report</h1>
        <p>Live financial reports from Xero accounting for each business entity</p>
      </div>

      {connectError && (
        <Alert
          message="Xero Connection Error"
          description={connectError}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
          onClose={() => setConnectError(null)}
        />
      )}

      <Spin spinning={statusLoading}>
        {!statusLoading && !isConnected && (
          <Card style={{ marginBottom: 16, textAlign: 'center' }}>
            <Result
              icon={<LinkOutlined style={{ color: '#54A2CC' }} />}
              title="Connect to Xero"
              subTitle="Connect your Xero account to view financial reports for ShineHub, PW Renewable, and GTL Renewable."
              extra={
                canManageConnection ? (
                  <Button type="primary" size="large" icon={<LinkOutlined />} onClick={handleConnect}>
                    Connect to Xero
                  </Button>
                ) : (
                  <p>Please ask an administrator to connect the Xero account.</p>
                )
              }
            />
          </Card>
        )}

        {isConnected && (
          <>
            {canManageConnection && (
              <Card size="small" style={{ marginBottom: 16 }}>
                <Space>
                  <Tag color="green">Connected</Tag>
                  <span style={{ fontSize: 12, color: '#888' }}>
                    {tenants.length} organization{tenants.length !== 1 ? 's' : ''} linked
                  </span>
                  <Button size="small" danger icon={<DisconnectOutlined />} onClick={handleDisconnect}>
                    Disconnect
                  </Button>
                </Space>
              </Card>
            )}

            {tabItems.length > 0 ? (
              <Tabs defaultActiveKey={tabItems[0]?.key} items={tabItems} />
            ) : (
              <Alert
                message="No organizations found"
                description="The Xero connection has no linked organizations. Try disconnecting and reconnecting."
                type="warning"
                showIcon
              />
            )}
          </>
        )}
      </Spin>
    </PageLayout>
  );
}
